/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import clsx from 'clsx';
import { startsWith } from 'lodash';
import { useEffect, useState } from 'react';

// Mantine
import {
	ActionIcon,
	AppShell,
	Box,
	Flex,
	Image,
	NavLink,
} from '@mantine/core';
import {
	useDisclosure,
	useHover,
	useMediaQuery,
} from '@mantine/hooks';

// Routing
import { Outlet, useNavigate } from 'react-router';

// Hooks
import { useLogout } from '@hooks/useLogout';
import { useLocation } from 'react-router';

// Config

// Styles
import classes from './main.module.css';

// Components
import { Loading } from '@components/atoms/Loading';
import { Text, Title } from '@components/v2/ui';
import { TopBar } from '@components/v2/ui/TopBar';

// Store
import { useTranslation } from 'react-i18next';

// Lib
import {
	findPriorityRole,
	useAuthorization,
} from '@lib/authorization';

import { useWhoAmI } from '@api/get-whoami';

import {
	DEMO,
	DEVELOP,
	LOCAL,
	NOT_APPLICABLE,
	STAGING,
} from '@config/constants/common';
import { AREC_ORGANIZATION_IDS } from '@config/constants/organizations';

import useStore from '@store/index';
import {
	IconChevronRight,
	IconX,
} from '@tabler/icons-react';

// Utils
import { getTitleFromPathname } from '@utils/functions';

import { useGetAllAdditionalities } from '@api/get-all-additionalities';
import { useStatus } from '@api/get-version';
import packageJson from '../../../../../package.json';

import { DefaultModal } from '@components/v2/ui/DefaultModal';
import {
	BUILD_SHA,
	IS_DEMO,
	IS_DEVELOP,
	IS_LOCAL,
	IS_PRODUCTION,
	IS_STAGING,
} from '@config/constants/env';
import { ROLES } from '@config/constants/roles';
import { sidebarItems } from './sidebar.config';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Main = () => {
	/* ********************************** Hooks ********************************* */
	const isBigScreen = useMediaQuery(
		'(min-width: 1440px)'
	);

	const { version, build } = useStatus();

	const isMobile = useMediaQuery('(max-width: 768px)');

	const {
		getUser,
		isAddOffset,
		setIsOpenModalCancelAddOffset,
		isAddProduction,
		setIsOpenModalCancelAddProduction,
	} = useStore();

	const { data: cachedUser, isPending: isPendingWhoAmI } =
		useWhoAmI();

	const producerRole = cachedUser?.roles.find((r) =>
		['PRODUCER_ADMIN', 'PRODUCER_STAFF'].includes(r.role)
	);

	const entityId = producerRole?.entity ?? null;

	const { hasAnyAcl, checkRole, checkAcls } =
		useAuthorization();

	const [isOpenModalLogout, setIsOpenModalLogout] =
		useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const logout = useLogout();
	const { t } = useTranslation();

	useEffect(() => {
		if (!isPendingWhoAmI && checkRole([ROLES.USER])) {
			if (
				location.pathname === '/' ||
				location.pathname === '/dashboard'
			) {
				navigate('/measurability-projects', {
					replace: true,
				});
			}
		}
	}, [
		isPendingWhoAmI,
		cachedUser,
		location.pathname,
		navigate,
	]);

	useEffect(() => {
		close();
	}, [isMobile]);

	const [opened, { toggle, close }] = useDisclosure();

	const [isCollapsedDesktop, setIsCollapsedDesktop] =
		useState(false);

	const { hovered: isHoveredSidebar, ref } = useHover();

	const configSidebar = sidebarItems
		.filter(
			(item) =>
				(!item.allowedAcls && !item.allowedRoles) ||
				(item.allowedAcls &&
					hasAnyAcl(item.allowedAcls)) ||
				checkRole(item.allowedRoles || [])
		)
		.map((item) => ({
			...item,
			links: item.links?.filter(
				(link) =>
					(item.allowedAcls &&
						link.allowedAcls &&
						checkAcls(link.allowedAcls)) ||
					checkRole(link.allowedRoles || [])
			),
		}));

	const [openedItems, setOpenedItems] = useState<
		Record<string, boolean>
	>({});
	const toggleItem = (id: string) => {
		setOpenedItems((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
	};

	const { data: additionalities } =
		useGetAllAdditionalities({
			params: {
				pageSize: 1000,
				...(entityId ? { entity: entityId } : {}),
			},
			queryConfig: {
				enabled: checkRole([
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
				]),
			},
		});

	const links = configSidebar.map((link, index) => {
		const hasActiveSubLink = link?.links?.some(
			(_link) => _link.link === location.pathname
		);

		const isOpened =
			(isMobile && hasActiveSubLink) ||
			openedItems[link?.label] ||
			(isCollapsedDesktop && hasActiveSubLink) ||
			(isHoveredSidebar && hasActiveSubLink) ||
			false;

		if (link?.links) {
			return (
				<NavLink
					key={index}
					opened={isOpened}
					noWrap
					className={classes.navLink}
					classNames={classes}
					label={t(link.label)}
					leftSection={<link.icon color="white" />}
					rightSection={
						(isHoveredSidebar ||
							isCollapsedDesktop ||
							isMobile) && (
							<IconChevronRight
								color="white"
								size="24px"
							/>
						)
					}
					childrenOffset={28}
					active={hasActiveSubLink}
					onClick={() => {
						toggleItem(link.label);
					}}
					style={
						isMobile ? { pointerEvents: 'auto' } : {}
					}
				>
					<div
						className={clsx(
							(isHoveredSidebar || isMobile) &&
								classes.subNavlink,
							!isHoveredSidebar &&
								isCollapsedDesktop &&
								!opened &&
								classes.subNavlink,
							opened && classes.subNavlink
						)}
					>
						{link.links.map((_link) => (
							<NavLink
								key={_link.link}
								active={_link.link === location.pathname}
								label={t(_link.label)}
								className={classes.navLink}
								classNames={classes}
								onClick={() => {
									if (
										isAddProduction &&
										startsWith(_link.link, '/productions')
									) {
										setIsOpenModalCancelAddProduction(
											true
										);
									}
									if (_link.link) navigate(_link.link);
									if (isMobile) toggle();
								}}
							/>
						))}
					</div>
				</NavLink>
			);
		}

		if (
			checkRole([
				ROLES.PRODUCER_ADMIN,
				ROLES.PRODUCER_STAFF,
			]) &&
			link?.link == '/additionality-projects' &&
			Array.isArray(additionalities?.rows) &&
			additionalities?.rows.length == 0
		) {
			return null;
		}
		return (
			<NavLink
				key={link.label}
				noWrap
				label={t(link.label)}
				leftSection={
					<link.icon color="white" size="24px" />
				}
				className={classes.navLink}
				classNames={classes}
				active={
					link.link === location.pathname ||
					(startsWith(
						location.pathname,
						'/measurability-projects'
					) &&
						link.link === '/measurability-projects') ||
					(startsWith(
						location.pathname,
						'/additionality-projects'
					) &&
						link.link === '/additionality-projects')
				}
				onClick={() => {
					if (
						isAddOffset &&
						startsWith(link.link, '/offsets')
					) {
						setIsOpenModalCancelAddOffset(true);
					}

					if (link.link) navigate(link.link);
					if (isMobile) toggle();
				}}
				style={isMobile ? { pointerEvents: 'auto' } : {}}
			/>
		);
	});

	/* ******************************** RENDERING ******************************* */
	if (isPendingWhoAmI) return <Loading />;

	return (
		<AppShell
			layout="alt"
			navbar={{
				width: isCollapsedDesktop ? 360 : 92,
				breakpoint: 'sm',
				collapsed: { mobile: !opened },
			}}
		>
			<AppShell.Navbar
				onMouseLeave={() => {
					if (!isCollapsedDesktop) setOpenedItems({});
				}}
				ref={ref}
				top={0}
				left={0}
				zIndex="97"
				bg="var(--mantine-color-igluPrimaryHover-9)"
				style={{
					border:
						'1px solid var(--mantine-color-igluPrimaryHover-9)',
					padding: '24px 20.5px',
					zIndex: '999px',
					width: isMobile
						? '100%'
						: isHoveredSidebar
							? '360px'
							: isCollapsedDesktop
								? '360px'
								: '92px',

					transition: 'width 250ms ease',
				}}
			>
				{cachedUser?.organization &&
				Object.values(AREC_ORGANIZATION_IDS).includes(
					cachedUser?.organization
				) ? (
					<Flex justify="space-between" align="center">
						<Flex gap="10px" align="center">
							<Image
								fit="fill"
								w="50px"
								h="100%"
								src="https://res.cloudinary.com/inuk/image/upload/v1749748165/inuk/IGLU/jyyxkyjwyn35xxlhhsom.png"
							/>
							{!isMobile ? (
								<Image
									className={clsx(
										!isHoveredSidebar &&
											!isCollapsedDesktop &&
											classes.logoTitle,
										isHoveredSidebar &&
											!isCollapsedDesktop &&
											classes.logoTitleHovered,
										isMobile && classes.logoTitleHovered
									)}
									fit="fill"
									w="73.52px"
									h="100%"
									src="https://res.cloudinary.com/inuk/image/upload/v1750153219/inuk/IGLU/viimjvdrohizichocimt.svg"
								/>
							) : (
								<Image
									fit="fill"
									w="73.52px"
									h="100%"
									src="https://res.cloudinary.com/inuk/image/upload/v1750153219/inuk/IGLU/viimjvdrohizichocimt.svg"
								/>
							)}
						</Flex>
						<ActionIcon
							c="var(--mantine-color-igluInnerForm-9)"
							variant="light"
							radius="xl"
							size={48}
							hiddenFrom="sm"
							onClick={toggle}
						>
							<IconX size={24} />
						</ActionIcon>
					</Flex>
				) : (
					<Flex justify="space-between" align="center">
						<Flex gap="10px" align="center">
							<Image
								fit="fill"
								w="50px"
								h="50px"
								src="https://res.cloudinary.com/inuk/image/upload/v1759249565/Modal_Model_1_rbs7dx.svg"
							/>
							{!isMobile ? (
								<Image
									className={clsx(
										!isHoveredSidebar &&
											!isCollapsedDesktop &&
											classes.logoTitle,
										isHoveredSidebar &&
											!isCollapsedDesktop &&
											classes.logoTitleHovered,
										isMobile && classes.logoTitleHovered
									)}
									fit="fill"
									src="https://res.cloudinary.com/inuk/image/upload/v1759250339/Iglu_jlg07w.svg"
								/>
							) : (
								<Image
									fit="fill"
									src="https://res.cloudinary.com/inuk/image/upload/v1759250339/Iglu_jlg07w.svg"
								/>
							)}
						</Flex>
						<ActionIcon
							c="var(--mantine-color-igluInnerForm-9)"
							variant="light"
							radius="xl"
							size={48}
							hiddenFrom="sm"
							onClick={toggle}
						>
							<IconX size={24} />
						</ActionIcon>
					</Flex>
				)}

				{IS_DEVELOP && !IS_PRODUCTION && (
					<div
						className={clsx(classes.pillDevelopClosed)}
					>
						{DEVELOP}
					</div>
				)}
				{IS_DEMO && !IS_PRODUCTION && (
					<div className={clsx(classes.pillDemoClosed)}>
						{DEMO}
					</div>
				)}
				{IS_STAGING && !IS_PRODUCTION && (
					<div
						className={clsx(classes.pillStagingClosed)}
					>
						{STAGING}
					</div>
				)}
				{IS_LOCAL && !IS_PRODUCTION && (
					<div className={clsx(classes.pillLocalClosed)}>
						{LOCAL}
					</div>
				)}

				<Box className={classes.links}>
					<div className={classes.linksInner}>
						{links}
					</div>
				</Box>
				<Flex
					style={{ position: 'relative' }}
					gap="10px"
					align="center"
					h={'45px'}
				>
					<Image
						fit="fill"
						w="50px"
						h="16.016px"
						src="https://res.cloudinary.com/inuk/image/upload/v1750153577/inuk/IGLU/nanmt78hbwnnd71b0lxh.svg"
					/>
					{/* For Production & Demo & Local */}
					{!IS_STAGING && (
						<div
							style={{
								position: 'absolute',
								top: 39,
								left: -13,
							}}
						>
							<Text
								c={'#485B73'}
								size="8px"
								fw={500}
								style={{
									whiteSpace: 'nowrap',
									lineHeight: '10px',
									letterSpacing: '0.16px',
								}}
							>
								IGLU {packageJson.version} - API {version}
							</Text>
						</div>
					)}
					{/* For Staging & Dev */}
					{IS_STAGING && !IS_PRODUCTION && (
						<div>
							<div
								style={{
									position: 'absolute',
									top: 39,
									left: -15,
								}}
							>
								<Text
									c={'#485B73'}
									size="8px"
									fw={500}
									style={{
										whiteSpace: 'nowrap',
										lineHeight: '10px',
										letterSpacing: '0.16px',
									}}
								>
									IGLU {packageJson.version} - {BUILD_SHA}
								</Text>
							</div>
							<div
								style={{
									position: 'absolute',
									top: 54,
									left: -11,
								}}
							>
								<Text
									c={'#485B73'}
									size="8px"
									fw={500}
									style={{
										whiteSpace: 'nowrap',
										lineHeight: '10px',
										letterSpacing: '0.16px',
									}}
								>
									API {version} - {build}
								</Text>
							</div>
						</div>
					)}
				</Flex>
			</AppShell.Navbar>
			<AppShell.Main
				style={{
					position: 'relative',

					display: 'flex',
					flexDirection: 'column',
				}}
				bg="var(--mantine-color-igluBackground-9)"
			>
				<DefaultModal
					opened={isOpenModalLogout}
					onConfirm={() => {
						logout();
						setIsOpenModalLogout(false);
					}}
					onClose={() => {
						setIsOpenModalLogout(false);
					}}
					title={t('global.logoutSureTitle')}
				/>
				<TopBar
					title={t(
						`${getTitleFromPathname(location.pathname)}`
					)}
					isMobile={isMobile}
					isBigScreen={isBigScreen}
					onToggleSidebar={toggle}
					avatarSrc={cachedUser?.avatar}
					userName={cachedUser?.firstName}
					userRole={
						cachedUser && cachedUser.roles
							? findPriorityRole(cachedUser.roles)?.role
							: NOT_APPLICABLE
					}
					onLogout={() => setIsOpenModalLogout(true)}
					onExpand={() => {
						setIsCollapsedDesktop(!isCollapsedDesktop);
						setOpenedItems({});
					}}
					isExpanded={isCollapsedDesktop}
				/>

				<Title
					c={'var(--mantine-color-igluPrimary-9)'}
					order={6}
					pl={'sm'}
					pt={'sm'}
					hiddenFrom="sm"
				>
					{t(
						`${getTitleFromPathname(location.pathname)}`
					)}
				</Title>
				<Box className={classes.content}>
					<Outlet />
				</Box>
			</AppShell.Main>
		</AppShell>
	);
};

export default Main;
